import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import React from "react";
import { css } from "styled-components";

const enum fields {
    showPartNumber = "showPartNumber",
    partNumberTitle = "partNumberTitle",
    showIndustryNumber = "showIndustryNumber",
    industryNumberTitle = "industryNumberTitle",
    showBrand = "showBrand",
    brandTitle = "brandTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showPartNumber]: boolean;
        [fields.partNumberTitle]: string;
        [fields.showIndustryNumber]: boolean;
        [fields.industryNumberTitle]: string;
        [fields.showBrand]: boolean;
        [fields.brandTitle]: string;
    };
}

type Props = OwnProps & WidgetProps & HasProduct;

export interface ProductDetailsProductInfosStyles {
    container?: GridContainerProps;
    sectionGridItem?: GridItemProps;
    title?: TypographyProps;
    text?: TypographyProps;
    brandStyles?: ProductBrandStyles;
}

export const productDetailsProductInfosStyles: ProductDetailsProductInfosStyles = {
    container: { gap: 0 },
    sectionGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            display: flex;
            flex-direction: column;
            padding-bottom: 10px;
        `,
    },
    title: {
        color: COLORS.text.dark,
        size: TYPOGRAPHY.size.base,
        css: css`
            display: block;
        `,
    },
    text: {
        color: COLORS.text.dark,
        size: TYPOGRAPHY.size.base,
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            display: block;
        `,
    },
    brandStyles: {
        nameText: {
            color: COLORS.text.dark,
            size: TYPOGRAPHY.size.base,
            weight: TYPOGRAPHY.weight.bold,
        },
        nameLink: {
            typographyProps: {
                size: TYPOGRAPHY.size.base,
            },
        },
    },
};

const styles = productDetailsProductInfosStyles;

const ProductDetailsProductInfos = ({ product, fields }: Props) => {
    if (!product.properties.industryNumber && !product.productNumber && !product.brand) {
        return null;
    }

    const productPartNumber = product.productNumber;
    const productIndustryNumber = product.properties?.industryNumber;
    const productBrand = product.brand;

    return (
        <GridContainer {...styles.container}>
            {productPartNumber && fields.showPartNumber && (
                <GridItem {...styles.sectionGridItem}>
                    <Typography {...styles.title}>{fields.partNumberTitle}</Typography>
                    <Typography {...styles.text}>{productPartNumber}</Typography>
                </GridItem>
            )}
            {productBrand && fields.showBrand && (
                <GridItem {...styles.sectionGridItem}>
                    <Typography {...styles.title}>{fields.brandTitle}</Typography>
                    <ProductBrand brand={productBrand} showLogo={false} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {productIndustryNumber && fields.showIndustryNumber && (
                <GridItem {...styles.sectionGridItem}>
                    <Typography {...styles.title}>{fields.industryNumberTitle}</Typography>
                    <Typography {...styles.text}>{productIndustryNumber}</Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

const storeProductDetailsProductInfos: WidgetModule = {
    component: withProduct(ProductDetailsProductInfos),
    definition: {
        displayName: "Part#, Industry#, Brand",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showPartNumber,
                displayName: "Show Part Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.partNumberTitle,
                displayName: "Part Number Title",
                editorTemplate: "TextField",
                defaultValue: "Part Number",
                isRequired: true,
                fieldType: "Translatable",
                isVisible: widget => widget.fields.showPartNumber,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.brandTitle,
                displayName: "Brand Title",
                editorTemplate: "TextField",
                defaultValue: "Brand",
                isRequired: true,
                fieldType: "Translatable",
                isVisible: widget => widget.fields.showBrand,
            },
            {
                name: fields.showIndustryNumber,
                displayName: "Show Industry Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.industryNumberTitle,
                displayName: "Industry Number Title",
                editorTemplate: "TextField",
                defaultValue: "Industry Number",
                isRequired: true,
                fieldType: "Translatable",
                isVisible: widget => widget.fields.showIndustryNumber,
            },
        ],
    },
};

export default storeProductDetailsProductInfos;
